import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm, useWatch } from "react-hook-form";
import { BaseFeeTiming } from "@api/shared-runtime/enums";
import { Temporal } from "temporal-polyfill";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  BASE_FEE_TIMING_LABELS,
  BILLING_TIMEZONE,
  firstDayOfMonth,
  formatPlanDate,
  type BillingPlanData,
  type BillingPlanVersion,
} from "./billing-plan-values";

type PlanInvoicingValues = {
  enabled: boolean;
  invoicingStartDate: string;
  baseFeeTiming: BaseFeeTiming;
};

function toPlanDate(instant: string): string {
  return Temporal.Instant.from(instant)
    .toZonedDateTimeISO(BILLING_TIMEZONE)
    .toPlainDate()
    .toString();
}

/**
 * A moved floor may start at the plan start or the current month, whichever
 * is later. The floor the plan already has stays valid even when it is older,
 * so the input accepts it as-is; the server rejects any other past date.
 */
function earliestInvoicingDate({
  plan,
  data,
}: {
  plan: BillingPlanVersion;
  data: BillingPlanData;
}): string {
  const planStart = toPlanDate(plan.effectiveStartsAt);
  const monthStart = firstDayOfMonth(data.currentMonth);
  const earliest = planStart > monthStart ? planStart : monthStart;
  if (plan.invoicingStartsAt) {
    const current = toPlanDate(plan.invoicingStartsAt);
    return current < earliest ? current : earliest;
  }
  return earliest;
}

/** Drafts for cycles the new settings exclude stay issuable until discarded. */
function describeOrphanedDrafts({
  plan,
  enabled,
  invoicingStartDate,
}: {
  plan: BillingPlanVersion;
  enabled: boolean;
  invoicingStartDate: string;
}): string | null {
  if (plan.draftInvoiceCount === 0) {
    return null;
  }
  const currentFloor = plan.invoicingStartsAt
    ? toPlanDate(plan.invoicingStartsAt)
    : null;
  const movedLater =
    enabled && currentFloor !== null && invoicingStartDate > currentFloor;
  if (!enabled || movedLater) {
    const drafts = `${plan.draftInvoiceCount} unissued draft${plan.draftInvoiceCount === 1 ? "" : "s"}`;
    return `This plan has ${drafts}. ${enabled ? "Drafts for cycles before the new date" : "They"} stay issuable; discard them from the Invoices panel if they should not be billed.`;
  }
  return null;
}

export function PlanInvoicingDialog({
  plan,
  data,
  onSaved,
  onBusyChange,
  onReload,
  busy,
}: {
  plan: BillingPlanVersion;
  data: BillingPlanData;
  onSaved: () => Promise<void>;
  onBusyChange: (busy: boolean) => void;
  onReload: () => void;
  busy: boolean;
}) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const earliest = earliestInvoicingDate({ plan, data });
  const form = useForm<PlanInvoicingValues>({
    defaultValues: {
      enabled: plan.invoicingStartsAt !== null,
      invoicingStartDate: plan.invoicingStartsAt
        ? toPlanDate(plan.invoicingStartsAt)
        : earliest,
      baseFeeTiming: plan.baseFeeTiming,
    },
  });
  const enabled = useWatch({ control: form.control, name: "enabled" });
  const invoicingStartDate = useWatch({
    control: form.control,
    name: "invoicingStartDate",
  });
  const timingLocked = plan.invoiceCount > 0;
  const orphanedDrafts = describeOrphanedDrafts({
    plan,
    enabled,
    invoicingStartDate,
  });
  const mutation = useMutation(
    trpc.adminBillingPlans.updatePlanInvoicing.mutationOptions({
      onSuccess: onSaved,
    }),
  );

  async function save(values: PlanInvoicingValues) {
    if (mutation.isPending) {
      return;
    }
    onBusyChange(true);
    try {
      await mutation.mutateAsync({
        organizationBusinessUnitId: data.unit.id,
        expectedVersion: data.version,
        planId: plan.id,
        invoicingStartDate: values.enabled ? values.invoicingStartDate : null,
        baseFeeTiming: timingLocked ? undefined : values.baseFeeTiming,
      });
      setOpen(false);
    } catch {
      // Stale submissions show the server's conflict and a reload action below.
    } finally {
      onBusyChange(false);
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!mutation.isPending) {
          setOpen(nextOpen);
        }
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={busy}>
          Invoicing
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Invoicing for this plan</AlertDialogTitle>
          <AlertDialogDescription>
            Cycles starting on or after the chosen date produce invoice drafts.
            Earlier cycles are never invoiced; usage stays visible in reports.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form onSubmit={form.handleSubmit(save)} className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
            <Label htmlFor="plan-invoicing-enabled" className="font-medium">
              Generate invoices for this plan
            </Label>
            <Controller
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <Switch
                  id="plan-invoicing-enabled"
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    mutation.reset();
                    field.onChange(checked);
                  }}
                  disabled={mutation.isPending}
                />
              )}
            />
          </div>
          {enabled && (
            <div className="space-y-2">
              <Label htmlFor="plan-invoicing-start">Invoice cycles from</Label>
              <Input
                id="plan-invoicing-start"
                type="date"
                required
                min={earliest}
                disabled={mutation.isPending}
                {...form.register("invoicingStartDate", {
                  onChange: () => mutation.reset(),
                })}
              />
              <p className="text-sm text-muted-foreground">
                No earlier than {formatPlanDate(earliest)}: past cycles are
                never invoiced retroactively.
              </p>
            </div>
          )}
          {orphanedDrafts && (
            <p
              role="alert"
              className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            >
              {orphanedDrafts}
            </p>
          )}
          {timingLocked && (
            // Once an invoice exists the timing decided what it carried; the
            // only remaining freedom is pausing or resuming drafts.
            <p className="text-sm text-muted-foreground">
              {BASE_FEE_TIMING_LABELS[plan.baseFeeTiming]}. Fixed: this plan
              already has {plan.invoiceCount} invoice
              {plan.invoiceCount === 1 ? "" : "s"}. Schedule new terms to bill
              differently.
            </p>
          )}
          {!timingLocked && enabled && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Base fee billing</legend>
              <Controller
                control={form.control}
                name="baseFeeTiming"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    disabled={mutation.isPending}
                    onValueChange={(value) => {
                      mutation.reset();
                      field.onChange(value);
                    }}
                    className="gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value={BaseFeeTiming.InAdvance}
                        id="plan-invoicing-timing-advance"
                      />
                      <Label htmlFor="plan-invoicing-timing-advance">
                        At the start of the cycle
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value={BaseFeeTiming.InArrears}
                        id="plan-invoicing-timing-arrears"
                      />
                      <Label htmlFor="plan-invoicing-timing-arrears">
                        After the cycle ends
                      </Label>
                    </div>
                  </RadioGroup>
                )}
              />
            </fieldset>
          )}
          {mutation.error && (
            <div className="space-y-2">
              <p role="alert" className="text-sm text-red-700">
                {mutation.error.message}
              </p>
              {mutation.error.data?.code === "CONFLICT" && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onReload}
                  disabled={mutation.isPending}
                >
                  Reload latest plan
                </Button>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Saving…" : "Save invoicing"}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
